/**
 * English: Map ChatTest WebSocket messages and agent statuses into graph view props.
 * Thai: แปลงข้อความ WS และสถานะ agent เป็นข้อมูลสำหรับกราฟ
 */
import { ChatMessage, AgentLiveStatus } from '../types';
import type { SpeakingAgent, TypingFeedEntry } from './agentNodeDiscussion';
import { parseChatTimestampMs } from './chatTimestamp';

const HIDDEN_GRAPH_MESSAGE_TYPES = new Set(['master_routing', 'status']);

type GraphSourceMessage = Pick<
  ChatMessage,
  'agent_id' | 'agent_name' | 'content' | 'message_type' | 'timestamp'
>;

function toNodeEntry(
  msg: GraphSourceMessage,
  timestampOverride?: number,
): TypingFeedEntry | null {
  if (!msg.content?.trim()) return null;
  if (HIDDEN_GRAPH_MESSAGE_TYPES.has(msg.message_type || '')) return null;
  const parsed = parseChatTimestampMs(msg.timestamp);
  const timestamp = timestampOverride ?? (Number.isFinite(parsed) ? parsed : Date.now());
  return {
    agent_id: msg.agent_id || '',
    agent_name: msg.agent_name,
    content: msg.content,
    message_type: msg.message_type || '',
    timestamp,
  };
}

/** @deprecated Prefer buildGraphNodeMessages — survives task_started WS clears via history merge */
export function wsMessagesToNodeMessages(messages: ChatMessage[]): TypingFeedEntry[] {
  const now = Date.now();
  return messages
    .map((msg) => toNodeEntry(msg, now))
    .filter((entry): entry is TypingFeedEntry => entry !== null);
}

/** Merge transcript history with live WS frames so graph bubbles survive task_started clears. */
export function buildGraphNodeMessages(
  wsMessages: ChatMessage[],
  historyMessages: GraphSourceMessage[],
): TypingFeedEntry[] {
  const now = Date.now();
  const seen = new Set<string>();
  const merged: TypingFeedEntry[] = [];

  const push = (entry: TypingFeedEntry | null) => {
    if (!entry) return;
    const key = `${entry.agent_id}|${entry.message_type}|${entry.content}|${entry.timestamp}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(entry);
  };

  historyMessages.forEach((msg) => {
    if (msg.agent_id === 'user') return;
    push(toNodeEntry(msg));
  });

  wsMessages.forEach((msg) => {
    if (msg.agent_id === 'user') return;
    // Client receive time keeps TTL valid while server timestamps may be seconds or stale.
    push(toNodeEntry(msg, now));
  });

  merged.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
  return merged.slice(-40);
}

function mapLiveStatus(status: AgentLiveStatus): SpeakingAgent['status'] {
  if (status === 'completed') return 'done';
  return status;
}

export function agentStatusesToSpeakers(
  statuses: Record<string, AgentLiveStatus>,
  agentNames: Record<string, string>,
): SpeakingAgent[] {
  return Object.entries(statuses).map(([id, status]) => ({
    id,
    name: agentNames[id] || id,
    status: mapLiveStatus(status),
  }));
}
